/**
 * Snaggedify Outbound Affiliate Engine — Section 1 & 2.
 * Real per-network affiliate tag injection, resolved entirely client-side (no backend
 * round-trip) so every "Snag Deal" tap, feed-card click, and map redirect opens
 * instantly with the correct tracking tag already applied.
 */

// Section 1 — Network keys/tags. These are public, per-click identifiers (the same
// ones any visitor can see in the final outbound URL) — that's how affiliate tagging
// always works, unlike a server secret, so they live here rather than in .env.
export const AFFILIATE_CONFIG = {
  AMAZON_TAG: "snaggedify-20",
  SOVRN_KEY: "0dae52b54c2938afb94dd09ac3d39eae",
  CJ_PROPERTY_ID: "101873062",
  IMPACT_SID: "7702024",
  SKIMLINKS_ID: "308393X1796702",
};

/**
 * Section 2 — Dynamic Outbound Re-routing. Priority: Amazon > CJ > Impact > Sovrn
 * (general retail fallback, covers the long tail of independent resellers). Maps/
 * navigation links are never wrapped — there's no retail commission on directions,
 * and wrapping would break the native Maps handoff.
 */
export function buildAffiliateUrl(destinationUrl, merchantType = "") {
  if (!destinationUrl) return "#";

  let target;
  try {
    target = new URL(destinationUrl, window.location.origin);
  } catch {
    return destinationUrl; // relative/malformed — pass through untouched
  }

  if (target.protocol !== "http:" && target.protocol !== "https:") return destinationUrl; // maps://, mailto:, etc.

  const hostname = target.hostname.toLowerCase();
  const type = merchantType.toLowerCase();
  if (hostname.includes("maps.google.") || hostname.includes("maps.apple.")) return destinationUrl;

  // 1. Amazon Associates
  if (hostname.includes("amazon.") || type === "amazon") {
    if (!target.searchParams.has("tag")) target.searchParams.set("tag", AFFILIATE_CONFIG.AMAZON_TAG);
    return target.toString();
  }

  // 2. CJ Affiliate (e.g., Walmart, Best Buy)
  if (type === "cj" || hostname.includes("walmart.") || hostname.includes("bestbuy.")) {
    return `https://www.anrdoezrs.net/click-${AFFILIATE_CONFIG.CJ_PROPERTY_ID}-url-${encodeURIComponent(target.toString())}`;
  }

  // 3. Impact.com (local pickup / digital service partners)
  if (type === "impact" || hostname.includes("impactradius")) {
    target.searchParams.set("irg1", AFFILIATE_CONFIG.IMPACT_SID);
    return target.toString();
  }

  // 4. Sovrn Commerce (formerly VigLink) — general retail fallback for every other
  // reseller domain, which is most of Snaggedify's actual catalog.
  return `https://redirect.viglink.com?key=${AFFILIATE_CONFIG.SOVRN_KEY}&u=${encodeURIComponent(target.toString())}`;
}

// Global Skimlinks fallback — a passive, sitewide script that only matters for real
// <a href> anchor clicks it can auto-detect; Snaggedify's "Snag Deal"/card taps are
// JS-driven buttons that always resolve through buildAffiliateUrl() above BEFORE
// window.open() fires, so by the time a click happens the URL is already fully
// tagged (Amazon/CJ/Impact/Sovrn) — Skimlinks never gets a chance to double-wrap it.
let skimlinksInjected = false;
export function injectSkimlinksTracker() {
  if (typeof document === "undefined" || skimlinksInjected || document.getElementById("skimlinks-script")) return;
  skimlinksInjected = true;
  const script = document.createElement("script");
  script.id = "skimlinks-script";
  script.type = "text/javascript";
  script.async = true;
  script.src = `https://s.skimresources.com/js/${AFFILIATE_CONFIG.SKIMLINKS_ID}.skimlinks.js`;
  document.head.appendChild(script);
}
